// Pure rendering helpers for the Tiger Adventure canvas game.
// No React, no audio, no game state — only takes a 2D context and inputs and draws.

export const VIEW_W = 960;
export const VIEW_H = 480;
export const GROUND_Y = 380;

export const STATE = {
  IDLE: "idle",
  RUN: "run",
  JUMP: "jump",
  EAT: "eat",
  DRINK: "drink",
  BACKFLIP: "backflip",
  ROAR: "roar",
  HURT: "hurt",
};

export function drawBackground(ctx, level, scrollX) {
  // Sky gradient
  const grad = ctx.createLinearGradient(0, 0, 0, VIEW_H);
  grad.addColorStop(0, level.sky[0]);
  grad.addColorStop(1, level.sky[1]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);

  if (level.type === "space") {
    ctx.fillStyle = "#fff";
    for (let i = 0; i < 80; i++) {
      const x = (i * 73 + scrollX * 0.2) % VIEW_W;
      const y = (i * 41) % (VIEW_H - 100);
      ctx.beginPath();
      ctx.arc(x, y, (i % 3) + 1, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "#FFD166";
    ctx.beginPath();
    ctx.arc(VIEW_W - 120, 110, 40, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#A06CD5";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.ellipse(VIEW_W - 120, 110, 60, 14, 0.3, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    ctx.fillStyle = "#FFD166";
    ctx.beginPath();
    ctx.arc(120, 90, 36, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#281E12";
    ctx.lineWidth = 4;
    ctx.stroke();
  }

  drawDecor(ctx, level, scrollX);

  ctx.fillStyle = level.ground;
  ctx.fillRect(0, GROUND_Y, VIEW_W, VIEW_H - GROUND_Y);
  ctx.fillStyle = level.groundDark;
  ctx.fillRect(0, GROUND_Y, VIEW_W, 12);
  ctx.strokeStyle = level.groundDark;
  ctx.lineWidth = 2;
  for (let i = 0; i < 40; i++) {
    const x = (i * 50 - (scrollX % 50)) % VIEW_W;
    ctx.beginPath();
    ctx.moveTo(x, GROUND_Y + 24);
    ctx.lineTo(x + 14, GROUND_Y + 24);
    ctx.stroke();
  }
}

export function drawDecor(ctx, level, scrollX) {
  const layer = (speed, draw) => {
    const offset = (scrollX * speed) % 320;
    for (let i = -1; i < Math.ceil(VIEW_W / 320) + 1; i++) {
      ctx.save();
      ctx.translate(i * 320 - offset, 0);
      draw(ctx);
      ctx.restore();
    }
  };

  switch (level.decor) {
    case "palm":
      layer(0.3, (c) => {
        c.fillStyle = "#5C3A1B";
        c.fillRect(140, 200, 14, 180);
        c.fillStyle = level.accents[0];
        c.beginPath();
        c.ellipse(147, 200, 70, 24, 0.2, 0, Math.PI * 2);
        c.ellipse(147, 200, 70, 24, -0.2, 0, Math.PI * 2);
        c.ellipse(147, 200, 70, 24, 1.4, 0, Math.PI * 2);
        c.fill();
      });
      layer(0.6, (c) => {
        c.fillStyle = level.accents[1];
        c.beginPath();
        c.arc(60, 360, 14, 0, Math.PI * 2);
        c.arc(220, 365, 18, 0, Math.PI * 2);
        c.fill();
      });
      break;
    case "grass":
      layer(0.4, (c) => {
        c.strokeStyle = level.accents[1];
        c.lineWidth = 4;
        for (let i = 0; i < 6; i++) {
          c.beginPath();
          c.moveTo(40 + i * 50, 380);
          c.lineTo(50 + i * 50, 320);
          c.stroke();
        }
      });
      break;
    case "mangrove":
      layer(0.4, (c) => {
        c.fillStyle = "#5C3A1B";
        c.fillRect(80, 240, 14, 140);
        c.fillRect(120, 260, 14, 120);
        c.fillStyle = level.accents[0];
        c.beginPath();
        c.arc(100, 230, 50, 0, Math.PI * 2);
        c.fill();
      });
      break;
    case "pine":
      layer(0.4, (c) => {
        c.fillStyle = "#1F3A1A";
        c.beginPath();
        c.moveTo(80, 380);
        c.lineTo(140, 200);
        c.lineTo(200, 380);
        c.closePath();
        c.fill();
        c.fillStyle = "#fff";
        c.beginPath();
        c.moveTo(110, 320);
        c.lineTo(140, 220);
        c.lineTo(170, 320);
        c.closePath();
        c.fill();
      });
      break;
    case "tree":
      layer(0.4, (c) => {
        c.fillStyle = "#5C3A1B";
        c.fillRect(120, 250, 18, 130);
        c.fillStyle = level.accents[0];
        c.beginPath();
        c.arc(129, 230, 60, 0, Math.PI * 2);
        c.fill();
      });
      break;
    case "house":
      layer(0.4, (c) => {
        c.fillStyle = "#FFE3A3";
        c.fillRect(80, 250, 140, 130);
        c.fillStyle = "#C0392B";
        c.beginPath();
        c.moveTo(70, 250);
        c.lineTo(150, 180);
        c.lineTo(230, 250);
        c.closePath();
        c.fill();
        c.fillStyle = "#5C3A1B";
        c.fillRect(130, 310, 40, 70);
      });
      break;
    case "school":
      layer(0.4, (c) => {
        c.fillStyle = "#E63946";
        c.fillRect(60, 220, 200, 160);
        c.fillStyle = "#fff";
        for (let i = 0; i < 3; i++) {
          for (let j = 0; j < 4; j++) {
            c.fillRect(80 + j * 45, 240 + i * 45, 30, 30);
          }
        }
      });
      break;
    case "mall":
      layer(0.4, (c) => {
        c.fillStyle = "#F72585";
        c.fillRect(40, 200, 240, 180);
        c.fillStyle = "#FFD166";
        c.fillRect(60, 220, 200, 30);
        c.fillStyle = "#fff";
        for (let i = 0; i < 5; i++) {
          c.fillRect(70 + i * 38, 270, 30, 100);
        }
      });
      break;
    case "park":
      layer(0.4, (c) => {
        c.fillStyle = "#5C3A1B";
        c.fillRect(120, 240, 16, 140);
        c.fillStyle = "#2D6A4F";
        c.beginPath();
        c.arc(128, 220, 56, 0, Math.PI * 2);
        c.fill();
        c.fillStyle = "#FFB347";
        c.fillRect(220, 320, 60, 60);
      });
      break;
    case "stars":
      layer(0.5, (c) => {
        c.fillStyle = "#A06CD5";
        c.beginPath();
        c.arc(100, 340, 30, 0, Math.PI * 2);
        c.fill();
      });
      break;
    default:
      break;
  }
}

export function drawTiger(ctx, tiger, t) {
  const { x, y, state, dir, frame } = tiger;
  ctx.save();
  ctx.translate(x, y);
  if (dir === -1) ctx.scale(-1, 1);

  if (state === STATE.BACKFLIP) {
    ctx.translate(0, -10);
    ctx.rotate(tiger.flipAngle || 0);
  }
  if (state === STATE.HURT) ctx.rotate(Math.sin(t / 50) * 0.1);

  const bounce = state === STATE.RUN ? Math.sin(frame * 0.3) * 3 : 0;
  const tailWag = Math.sin(frame * 0.2) * 0.3;

  // Tail
  ctx.strokeStyle = "#FF8C00";
  ctx.lineCap = "round";
  ctx.lineWidth = 10;
  ctx.beginPath();
  ctx.moveTo(-22, -8 + bounce);
  ctx.quadraticCurveTo(-46, -20 + bounce, -42 + tailWag * 6, -38 + bounce);
  ctx.stroke();
  ctx.strokeStyle = "#281E12";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-30, -16 + bounce);
  ctx.lineTo(-26, -22 + bounce);
  ctx.stroke();

  // Body
  ctx.fillStyle = "#FF8C00";
  ctx.strokeStyle = "#281E12";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(0, 0 + bounce, 28, 18, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Body stripes
  ctx.lineWidth = 3;
  for (let i = -2; i <= 2; i++) {
    ctx.beginPath();
    ctx.moveTo(i * 9, -16 + bounce);
    ctx.lineTo(i * 9 + 2, 12 + bounce);
    ctx.stroke();
  }

  // Legs
  const legSwing = state === STATE.RUN ? Math.sin(frame * 0.4) * 6 : 0;
  ctx.fillStyle = "#FF8C00";
  ctx.fillRect(-18, 12 + bounce, 8, 14 + legSwing);
  ctx.fillRect(8, 12 + bounce, 8, 14 - legSwing);
  ctx.strokeRect(-18, 12 + bounce, 8, 14 + legSwing);
  ctx.strokeRect(8, 12 + bounce, 8, 14 - legSwing);

  // Head
  const headOffset = state === STATE.EAT ? 4 : state === STATE.DRINK ? 6 : 0;
  ctx.fillStyle = "#FF8C00";
  ctx.beginPath();
  ctx.ellipse(20, -10 + bounce + headOffset, 16, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Ears
  ctx.beginPath();
  ctx.arc(11, -22 + bounce + headOffset, 5, 0, Math.PI * 2);
  ctx.arc(28, -22 + bounce + headOffset, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Muzzle
  ctx.fillStyle = "#FFF6E0";
  ctx.beginPath();
  ctx.ellipse(26, -6 + bounce + headOffset, 8, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Eye
  ctx.fillStyle = "#281E12";
  if (state === STATE.HURT) {
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(20, -16 + bounce + headOffset);
    ctx.lineTo(26, -10 + bounce + headOffset);
    ctx.moveTo(26, -16 + bounce + headOffset);
    ctx.lineTo(20, -10 + bounce + headOffset);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(23, -13 + bounce + headOffset, 2.4, 0, Math.PI * 2);
    ctx.fill();
  }

  // Mouth
  ctx.strokeStyle = "#281E12";
  ctx.lineWidth = 2;
  if (state === STATE.ROAR) {
    ctx.fillStyle = "#A82424";
    ctx.beginPath();
    ctx.ellipse(28, -4 + bounce + headOffset, 6, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = "#FFD166";
    ctx.lineWidth = 3;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(40, -8 + bounce + headOffset, 8 + i * 8 + Math.sin(t / 80) * 2, -0.6, 0.6);
      ctx.stroke();
    }
  } else if (state === STATE.EAT) {
    ctx.fillStyle = "#A82424";
    ctx.beginPath();
    ctx.ellipse(28, -4 + bounce + headOffset, 4 + Math.sin(t / 60) * 2, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(28, -2 + bounce + headOffset);
    ctx.lineTo(28, 1 + bounce + headOffset);
    ctx.stroke();
  }

  ctx.restore();
}

export function drawHunter(ctx, h) {
  ctx.save();
  ctx.translate(h.x, h.y);
  ctx.fillStyle = "#5A3A22";
  ctx.fillRect(-10, -30, 20, 36);
  ctx.strokeStyle = "#281E12";
  ctx.lineWidth = 3;
  ctx.strokeRect(-10, -30, 20, 36);
  ctx.fillStyle = "#F2C2A0";
  ctx.beginPath();
  ctx.arc(0, -42, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#3A5A2C";
  ctx.fillRect(-14, -52, 28, 6);
  ctx.fillRect(-8, -58, 16, 8);
  ctx.strokeRect(-14, -52, 28, 6);
  ctx.strokeRect(-8, -58, 16, 8);
  ctx.fillStyle = "#281E12";
  ctx.fillRect(-5, -45, 3, 3);
  ctx.fillRect(2, -45, 3, 3);
  ctx.fillStyle = "#281E12";
  ctx.fillRect(-9, 6, 6, 14);
  ctx.fillRect(3, 6, 6, 14);
  ctx.strokeStyle = "#281E12";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(8, -20);
  ctx.lineTo(28, -10);
  ctx.stroke();
  ctx.fillStyle = "#FFD166";
  ctx.beginPath();
  ctx.arc(32, -8, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export function drawPickup(ctx, p, t) {
  ctx.save();
  ctx.translate(p.x, p.y + Math.sin(t / 200 + p.x) * 3);
  if (p.type === "meat") {
    ctx.fillStyle = "#C0392B";
    ctx.beginPath();
    ctx.ellipse(0, 0, 11, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#281E12";
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.fillStyle = "#FFF6E0";
    ctx.fillRect(-3, -3, 6, 3);
    ctx.strokeRect(-3, -3, 6, 3);
  } else {
    ctx.fillStyle = "#45CB85";
    ctx.beginPath();
    ctx.moveTo(0, -10);
    ctx.bezierCurveTo(8, -4, 8, 8, 0, 10);
    ctx.bezierCurveTo(-8, 8, -8, -4, 0, -10);
    ctx.fill();
    ctx.strokeStyle = "#281E12";
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }
  ctx.restore();
}
